import { createSlice } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  updateDoc,
} from "firebase/firestore";
import {
  buildContentBoxPresentQueries,
  buildContentBoxScopeKey,
  mapSnapshotToContentBoxes,
  mergeContentBoxDocuments,
  shouldSuppressContentBoxSnapshot,
} from "../utils/contentBoxPresentQuery";
import { formatFirestoreReadError } from "../utils/firestoreCacheFirstRead";
import {
  ensureFirestoreAuthReady,
  isFirestorePermissionDeniedError,
} from "../utils/firestoreAuthReady";
import {
  isBootstrapShellIncomplete,
  resolveBootstrapShellTerminalError,
  shouldContinueBootstrapShellRetry,
} from "../utils/bootstrapShellUtils";
import { onSnapshotWithMetadata } from "../utils/firestoreListeners";
import {
  shouldRequireMatchingAuthUid,
  subscribeWithFirestoreAuthRetry,
} from "../utils/firestoreAuthRetrySubscription";
import { isServerConfirmedSnapshot, parseSnapshotMetadata } from "../utils/firestoreSnapshotMetadata";
import { db, auth } from "../firebase"; // Ensure correct Firestore import
import { normalizeProductTourDates } from "../utils/productTour";
import { normalizeLegacyScheduleJuly2026ConversionState } from "../utils/legacyScheduleJuly2026Conversion";
import {
  sanitizeFirestoreDataForRedux,
} from "../utils/sanitizeFirestoreDataForRedux";
import { fetchScheduleCalendar, saveScheduleCalendar } from "../utils/scheduleCalendarFirestore";
import {
  fetchTeacherHubEvents,
  replaceTeacherHubEvents,
} from "../utils/teacherHubEventsFirestore";
import {
  fetchClassStyleSchemes,
  replaceClassStyleSchemes,
  deleteClassStyleSchemeById,
  upsertClassStyleScheme,
  mergeClassStyleSchemeIntoList,
} from "../utils/classStyleSchemesFirestore";
import {
  buildCustomSchemeDeleteFallbackUpdates,
} from "../utils/classStyleDefaults";
import { getScheduleRuntimeMode } from "../utils/scheduleSettings";
import { reportAppError } from "../utils/reportAppError";
import {
  getLocalMockSampleClassContentBoxes,
  getOrderOfClassesWithLocalMockSampleClass,
  maybeAppendLocalMockSampleClass,
} from "../utils/localMockSampleClass";
import {
  GETTING_STARTED_CLASS_ID,
  getGettingStartedContentBoxes,
  isGettingStartedClass,
} from "../utils/gettingStartedClass";

const INITIAL_SCHEDULING_LOAD = {
  scheduleCalendar: "idle",
  teacherHubEvents: "idle",
  classStyleSchemes: "idle",
};

const USER_SHELL_READ_RETRY_DELAYS_MS = [0, 1200, 3000];
const USER_SHELL_CLASS_SYNC_RETRY_DELAYS_MS = [0, 1200, 3000, 2000, 4000];

const waitForMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getFirebaseProjectLabel = () =>
  process.env.REACT_APP_FIREBASE_PROJECT_ID || "lesson-launchpad";

const ensureAuthTokenReady = async ({ forceRefresh = false } = {}) =>
  ensureFirestoreAuthReady(auth, { forceRefresh });

const waitForServerDocument = async (docRef, maxRetries = 6, delayMs = 400) => {
  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const snap = await getDocFromServer(docRef);
    if (snap.exists()) {
      return snap;
    }
    if (attempt < maxRetries - 1) {
      await waitForMs(delayMs);
    }
  }
  return null;
};

const resolveBootstrapUserShell = async (refs) => {
  const { userRef, classesRef } = refs;

  try {
    const userSnap = await waitForServerDocument(userRef);
    if (userSnap?.exists()) {
      const classesSnap = await getDocsFromServer(classesRef);
      return {
        userResult: {
          snap: userSnap,
          freshness: parseSnapshotMetadata(userSnap),
          servedFromCacheOnly: false,
        },
        classesResult: {
          snap: classesSnap,
          freshness: parseSnapshotMetadata(classesSnap),
          servedFromCacheOnly: false,
        },
      };
    }
  } catch (error) {
    console.warn("Bootstrap user server read failed:", formatFirestoreReadError(error));
    return { error };
  }

  return null;
};

const mapClassesSnapshot = (classesSnap) =>
  classesSnap.docs.map((classDoc) =>
    sanitizeFirestoreDataForRedux({
      classId: classDoc.id,
      ...classDoc.data(),
    })
  );

const readBootstrapShellFromServer = async (
  userRef,
  classesRef,
  uid,
  { forceAuthRefresh = false } = {}
) => {
  if (auth.currentUser?.uid && auth.currentUser.uid !== uid) {
    throw new Error(
      `Authenticated uid (${auth.currentUser.uid}) does not match bootstrap uid (${uid}).`
    );
  }

  await ensureAuthTokenReady({ forceRefresh: forceAuthRefresh });

  let lastReadError = null;
  let lastShellIncomplete = false;

  for (let attempt = 0; attempt < USER_SHELL_CLASS_SYNC_RETRY_DELAYS_MS.length; attempt += 1) {
    if (!shouldContinueBootstrapShellRetry({
      attempt,
      standardRetryCount: USER_SHELL_READ_RETRY_DELAYS_MS.length,
      lastReadError,
      lastShellIncomplete,
    })) {
      break;
    }

    const delayMs = USER_SHELL_CLASS_SYNC_RETRY_DELAYS_MS[attempt];
    if (delayMs > 0) {
      await waitForMs(delayMs);
    }

    // After a permission-denied miss, force-refresh the ID token so Firestore
    // picks up Auth credentials that were still propagating on the first try.
    const forceRefresh =
      forceAuthRefresh
      || (attempt > 0 && isFirestorePermissionDeniedError(lastReadError));
    await ensureAuthTokenReady({ forceRefresh });

    const resolved = await resolveBootstrapUserShell({ userRef, classesRef });

    if (resolved?.error) {
      lastReadError = resolved.error;
      continue;
    }

    const cacheFallbackPermissionError = [
      resolved?.userResult?.serverError,
      resolved?.classesResult?.serverError,
    ].find(isFirestorePermissionDeniedError);
    if (cacheFallbackPermissionError) {
      lastReadError = cacheFallbackPermissionError;
      continue;
    }

    lastReadError = null;
    lastShellIncomplete = false;

    if (resolved?.userResult?.snap?.exists()) {
      const userData = resolved.userResult.snap.data();
      const classesData = mapClassesSnapshot(resolved.classesResult.snap);

      if (isBootstrapShellIncomplete(userData, classesData)) {
        lastShellIncomplete = true;
        continue;
      }

      return resolved;
    }
  }

  const terminalError = resolveBootstrapShellTerminalError({
    lastReadError,
    lastShellIncomplete,
  });
  if (terminalError) {
    return {
      userResult: {
        error: terminalError,
      },
      classesResult: null,
    };
  }

  return {
    userResult: { snap: { exists: () => false } },
    classesResult: null,
  };
};

const sortClassesByOrder = (classesData, orderOfClasses) => {
  if (!orderOfClasses?.length) {
    return classesData;
  }

  return [...classesData].sort((a, b) => {
    const leftIndex = orderOfClasses.indexOf(a.classId);
    const rightIndex = orderOfClasses.indexOf(b.classId);
    const leftOrder = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const rightOrder = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
    return leftOrder - rightOrder;
  });
};

const withVirtualLocalMockSampleClass = (classes, user) =>
  maybeAppendLocalMockSampleClass(classes, { user });

const getOrderWithVirtualLocalMockSampleClass = (orderOfClasses, user) =>
  getOrderOfClassesWithLocalMockSampleClass(orderOfClasses, { user });

const filterShareableClasses = (classesData = []) =>
  (classesData || []).filter((classData) => !isGettingStartedClass(classData));

const filterShareableOrderOfClasses = (orderOfClasses = []) =>
  (orderOfClasses || []).filter((classId) => classId !== GETTING_STARTED_CLASS_ID);

const dispatchStaticContentBoxes = ({
  dispatch,
  classId,
  contentBoxes,
  scopeKey = null,
} = {}) => {
  dispatch(
    setClassContentBoxes({
      classId,
      contentBoxes,
    })
  );
  dispatch(
    setContentBoxesLoad({
      classId,
      status: "loaded",
      scopeKey,
      syncSource: "local",
      isFromCache: false,
      hasPendingWrites: false,
      lastServerSyncAt: null,
      expectedQueryCount: 0,
      receivedQueryCount: 0,
    })
  );
  return () => {};
};

const normalizeUserDataForRedux = (userData) => {
  if (userData == null || typeof userData !== "object") {
    return userData;
  }

  const sanitizedUserData = sanitizeFirestoreDataForRedux(userData);

  return {
    ...sanitizedUserData,
    // Keep domain-specific normalizers for structured nested state.
    productTour: normalizeProductTourDates(sanitizedUserData?.productTour),
    legacyScheduleJuly2026Conversion: normalizeLegacyScheduleJuly2026ConversionState(
      sanitizedUserData?.legacyScheduleJuly2026Conversion
    ),
  };
};

export const EMPTY_SCHEDULING_STATE = {
  scheduleCalendar: {},
  teacherHubEvents: [],
  classStyleSchemes: [],
};

/**
 * Merge user shell data with optional scheduling patches.
 * - schedulingPatch omitted: preserve existingUser scheduling fields
 * - resetScheduling true: clear all scheduling fields
 * - schedulingPatch partial: update only provided keys
 */
export const normalizeUserPayload = (userData, schedulingPatch, existingUser = null) => {
  const existingScheduleCalendar = existingUser?.settings?.scheduleCalendar || {};
  let scheduleCalendar = existingScheduleCalendar;
  let teacherHubEvents = existingUser?.teacherHubEvents || [];
  let classStyleSchemes = existingUser?.classStyleSchemes || [];

  if (schedulingPatch === null) {
    scheduleCalendar = {};
    teacherHubEvents = [];
    classStyleSchemes = [];
  } else if (schedulingPatch !== undefined) {
    if (Object.prototype.hasOwnProperty.call(schedulingPatch, "scheduleCalendar")) {
      scheduleCalendar = schedulingPatch.scheduleCalendar ?? {};
    }
    if (Object.prototype.hasOwnProperty.call(schedulingPatch, "teacherHubEvents")) {
      teacherHubEvents = schedulingPatch.teacherHubEvents ?? [];
    }
    if (Object.prototype.hasOwnProperty.call(schedulingPatch, "classStyleSchemes")) {
      classStyleSchemes = schedulingPatch.classStyleSchemes ?? [];
    }
  }

  const normalizedUserData = normalizeUserDataForRedux(userData);

  return {
    ...normalizedUserData,
    teacherHubEvents,
    classStyleSchemes,
    settings: {
      ...(normalizedUserData?.settings || {}),
      scheduleCalendar,
    },
  };
};

const prepareLoginPayload = (payload) => {
  const userData = payload?.userData ?? payload;
  const resetScheduling = payload?.resetScheduling === true;
  const schedulingPatch = resetScheduling
    ? null
    : (Object.prototype.hasOwnProperty.call(payload ?? {}, "scheduling")
      ? payload.scheduling
      : undefined);

  return {
    userData: normalizeUserDataForRedux(userData),
    schedulingPatch,
    resetScheduling,
  };
};

export const userSlice = createSlice({
  name: "user",
  initialState: {
    user: null,
    classes: [], // Storing class list separately
    selectedClassContentBoxes: {}, // Store contentBoxes per class
    contentBoxesLoad: {},
    bootstrapSync: null,
    status: "idle",
    error: null,
    schedulingLoad: { ...INITIAL_SCHEDULING_LOAD },
    schedulingLoadError: null,
  },
  reducers: {
    login: {
      reducer: (state, action) => {
        const { userData, schedulingPatch, resetScheduling } = action.payload;
        state.user = normalizeUserPayload(
          userData,
          resetScheduling ? null : schedulingPatch,
          resetScheduling ? null : state.user
        );
        state.classes = sortClassesByOrder(
          withVirtualLocalMockSampleClass(state.classes, state.user),
          getOrderWithVirtualLocalMockSampleClass(state.user?.orderOfClasses, state.user)
        );
      },
      prepare: (payload) => ({
        payload: prepareLoginPayload(payload),
      }),
    },
    logout: (state) => {
      state.user = null;
      state.classes = [];
      state.selectedClassContentBoxes = {};
      state.contentBoxesLoad = {};
      state.bootstrapSync = null;
      state.schedulingLoad = { ...INITIAL_SCHEDULING_LOAD };
      state.schedulingLoadError = null;
    },
    setBootstrapSync: (state, action) => {
      state.bootstrapSync = action.payload;
    },
    setSchedulingDomainStatus: (state, action) => {
      const { domain, status } = action.payload;
      if (domain && state.schedulingLoad[domain] !== undefined) {
        state.schedulingLoad[domain] = status;
      }
    },
    resetSchedulingLoad: (state) => {
      state.schedulingLoad = { ...INITIAL_SCHEDULING_LOAD };
      state.schedulingLoadError = null;
    },
    setSchedulingLoadError: (state, action) => {
      state.schedulingLoadError = action.payload;
    },
    setClasses: {
      reducer: (state, action) => {
        const classesWithLocalMock = withVirtualLocalMockSampleClass(action.payload, state.user);
        if (!state.user || !state.user.orderOfClasses) {
          state.classes = classesWithLocalMock; // No sorting if order list doesn't exist
          return;
        }

        const orderOfClasses = getOrderWithVirtualLocalMockSampleClass(state.user.orderOfClasses, state.user);

        // Sort classes based on orderOfClasses
        state.classes = sortClassesByOrder(classesWithLocalMock, orderOfClasses);
      },
      prepare: (classes) => ({
        payload: sanitizeFirestoreDataForRedux(classes),
      }),
    },
    setSingleClass: {
      reducer: (state, action) => {
        const updatedClass = action.payload;
        const index = state.classes.findIndex((c) => c.classId === updatedClass.classId);
        if (index !== -1) {
          state.classes[index] = updatedClass;
        } else {
          state.classes.push(updatedClass);
        }
      },
      prepare: (classData) => ({
        payload: sanitizeFirestoreDataForRedux(classData),
      }),
    },
    setClassContentBoxes: {
      reducer: (state, action) => {
        const { classId, contentBoxes } = action.payload;
        state.selectedClassContentBoxes[classId] = contentBoxes;
      },
      prepare: ({ classId, contentBoxes }) => ({
        payload: {
          classId,
          contentBoxes: sanitizeFirestoreDataForRedux(contentBoxes),
        },
      }),
    },
    setContentBoxesLoad: (state, action) => {
      const {
        classId,
        status,
        scopeKey,
        syncSource,
        isFromCache,
        hasPendingWrites,
        lastServerSyncAt,
        expectedQueryCount,
        receivedQueryCount,
      } = action.payload;
      if (!classId) return;
      const previous = state.contentBoxesLoad[classId];
      state.contentBoxesLoad[classId] = {
        status: status ?? previous?.status ?? "idle",
        scopeKey: scopeKey ?? previous?.scopeKey ?? null,
        syncSource: syncSource ?? previous?.syncSource ?? "unknown",
        isFromCache: isFromCache ?? previous?.isFromCache ?? false,
        hasPendingWrites:
          hasPendingWrites ?? previous?.hasPendingWrites ?? false,
        lastServerSyncAt:
          lastServerSyncAt ?? previous?.lastServerSyncAt ?? null,
        expectedQueryCount:
          expectedQueryCount ?? previous?.expectedQueryCount ?? null,
        receivedQueryCount:
          receivedQueryCount ?? previous?.receivedQueryCount ?? null,
      };
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const {
  login,
  logout,
  setClasses,
  setSingleClass,
  setClassContentBoxes,
  setContentBoxesLoad,
  setBootstrapSync,
  setError,
  setSchedulingDomainStatus,
  resetSchedulingLoad,
  setSchedulingLoadError,
} = userSlice.actions;
export const selectUser = (state) => state.user.user;
export const selectScheduleCalendar = (state) => state.user.user?.settings?.scheduleCalendar || {};
export const selectTeacherHubEvents = (state) => state.user.user?.teacherHubEvents || [];
export const selectClassStyleSchemes = (state) => state.user.user?.classStyleSchemes || [];
export const selectClasses = (state) => state.user.classes;
export const selectContentBoxesLoad = (classId) => (state) =>
  state.user.contentBoxesLoad[classId] || {
    status: "idle",
    scopeKey: null,
    syncSource: "unknown",
    isFromCache: false,
    hasPendingWrites: false,
    lastServerSyncAt: null,
    expectedQueryCount: null,
    receivedQueryCount: null,
  };
export const selectBootstrapSync = (state) => state.user.bootstrapSync;
export const selectSchedulingLoadStatus = (state) => state.user.schedulingLoad;
export const selectSchedulingLoadError = (state) => state.user.schedulingLoadError;
export const selectIsTeacherHubEventsReady = (state) =>
  state.user.schedulingLoad?.teacherHubEvents === "loaded";
export const TEACHER_HUB_EVENTS_NOT_LOADED_MESSAGE =
  "Events are still loading. Please wait before saving.";
export const SCHEDULE_CALENDAR_LOADING_MESSAGE = "Loading your saved schedule…";
export const SCHEDULE_CALENDAR_NOT_LOADED_MESSAGE =
  "Please wait before saving schedule changes.";
export const CLASS_STYLE_SCHEMES_LOADING_MESSAGE = "Loading your saved class styles…";
export const CLASS_STYLE_SCHEMES_NOT_LOADED_MESSAGE =
  "Please wait before saving class style changes.";
export const selectCanPersistScheduleCalendar = (state) =>
  ["loaded", "error"].includes(state.user.schedulingLoad?.scheduleCalendar);
export const selectCanPersistClassStyleSchemes = (state) =>
  state.user.schedulingLoad?.classStyleSchemes === "loaded";
export const selectIsScheduleCalendarReady = (state) => {
  const schedulingLoad = state.user.schedulingLoad;
  const runtimeMode = getScheduleRuntimeMode(state.user.user?.settings);
  const bootstrapSync = state.user.bootstrapSync;

  if (runtimeMode === "legacy") {
    return (
      isServerConfirmedSnapshot(bootstrapSync)
      && (schedulingLoad.scheduleCalendar === "loaded" || schedulingLoad.scheduleCalendar === "error")
    );
  }

  return (
    schedulingLoad.scheduleCalendar === "loaded"
    || schedulingLoad.scheduleCalendar === "error"
  );
};
const EMPTY_CLASS_CONTENT_BOXES = [];

export const selectClassContentBoxes = (classId) => (state) =>
  state.user.selectedClassContentBoxes[classId] || EMPTY_CLASS_CONTENT_BOXES;

export const buildClassContentBoxesMapForIds = (classIds, selectedClassContentBoxes) => {
  const map = {};
  (classIds || []).forEach((classId) => {
    if (classId) {
      map[classId] = selectedClassContentBoxes[classId] || EMPTY_CLASS_CONTENT_BOXES;
    }
  });
  return map;
};

export const selectClassContentBoxesMapForIds = (classIds) => (state) =>
  buildClassContentBoxesMapForIds(classIds, state.user.selectedClassContentBoxes);

export const classContentBoxesMapEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right) return left === right;
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
};

export const selectClass = (classId) => (state) => state.user.classes.find((cls) => cls.classId === classId);

export default userSlice.reducer;

// Tier 1 — blocking shell: user doc + class metadata only.
// Use server reads so sign-in never depends on a healthy IndexedDB cache.
export const bootstrapUserShell = (uid, options = {}) => async (dispatch) => {
  const { forceAuthRefresh = false } = options;

  if (!uid) {
    const missingUidError = new Error("Missing authenticated user ID.");
    dispatch(setError(missingUidError.message));
    throw missingUidError;
  }

  try {
    dispatch(resetSchedulingLoad());

    const userRef = doc(db, "usersNew", uid);
    const classesRef = collection(db, `usersNew/${uid}/classes`);

    const applyShell = (userSnap, classesSnap, freshness, { resetScheduling = false } = {}) => {
      if (!userSnap.exists()) {
        return null;
      }

      const userData = userSnap.data();
      const classesData = sortClassesByOrder(
        mapClassesSnapshot(classesSnap),
        userData.orderOfClasses
      );

      dispatch(
        login(
          resetScheduling
            ? { userData: { ...userData, uid }, resetScheduling: true }
            : { userData: { ...userData, uid } }
        )
      );
      dispatch(setClasses(classesData));
      dispatch(setBootstrapSync(freshness));
      dispatch(setError(null));
      return { userData, classesData };
    };

    const refreshShellFromServer = async () => {
      const { userResult, classesResult } = await readBootstrapShellFromServer(
        userRef,
        classesRef,
        uid,
        { forceAuthRefresh }
      );

      if (userResult?.error) {
        const readError = userResult.error;
        const message = readError?.code === "permission-denied"
          ? `Could not read your account (permission denied). Try signing out and back in. (${readError.code})`
          : `Could not load your account: ${formatFirestoreReadError(readError)}`;
        dispatch(setError(message));
        console.error("Bootstrap user read failed:", readError);
        throw readError;
      }

      if (!userResult?.snap?.exists()) {
        const projectLabel = getFirebaseProjectLabel();
        const userNotFoundError = new Error(
          `User document not found for uid: ${uid}. Confirm usersNew/${uid} exists in Firebase project "${projectLabel}".`
        );
        dispatch(setError(userNotFoundError.message));
        console.error("User not found!", userNotFoundError);
        throw userNotFoundError;
      }

      const shell = applyShell(userResult.snap, classesResult.snap, userResult.freshness);
      void dispatch(ensureUserSchedulingDomains(uid, { force: true }));
      return { ...shell, servedFromCacheOnly: userResult.servedFromCacheOnly };
    };

    return refreshShellFromServer();
  } catch (error) {
    dispatch(setError(error?.message || "Error fetching user data"));
    console.error("Error bootstrapping user shell:", error);
    throw error;
  }
};

const shouldSkipSchedulingDomainLoad = (getState, domain, force = false) => {
  if (force) return false;
  const status = getState().user.schedulingLoad?.[domain];
  return status === "loaded" || status === "loading";
};

export const ensureScheduleCalendarLoaded = (uid, options = {}) => async (dispatch, getState) => {
  const { force = false, loadForLegacy = false } = options;
  if (!uid) return null;

  const user = selectUser(getState());
  if (user?.uid && user.uid !== uid) return null;
  if (shouldSkipSchedulingDomainLoad(getState, "scheduleCalendar", force)) {
    return user?.settings?.scheduleCalendar || {};
  }

  const bootstrapSync = getState().user.bootstrapSync;
  const runtimeMode = getScheduleRuntimeMode(user?.settings);

  if (runtimeMode === "legacy" && !loadForLegacy) {
    if (!isServerConfirmedSnapshot(bootstrapSync)) {
      return null;
    }
    dispatch(setSchedulingDomainStatus({ domain: "scheduleCalendar", status: "loaded" }));
    return {};
  }

  // A forced refresh of an already-loaded calendar (e.g. the post-save server
  // sync) must not flip the status back to "loading" — that would make
  // selectCanPersistScheduleCalendar false and reject saves that arrive while
  // the refresh is in flight.
  const previousStatus = getState().user.schedulingLoad?.scheduleCalendar;
  const isBackgroundRefresh = force && previousStatus === "loaded";
  if (!isBackgroundRefresh) {
    dispatch(setSchedulingDomainStatus({ domain: "scheduleCalendar", status: "loading" }));
  }

  try {
    const scheduleCalendar = await fetchScheduleCalendar(uid, { userSettings: user?.settings });
    const currentUser = selectUser(getState());
    if (!currentUser || currentUser.uid !== uid) return null;

    dispatch(patchUserScheduleCalendar(scheduleCalendar));
    dispatch(setSchedulingDomainStatus({ domain: "scheduleCalendar", status: "loaded" }));
    return scheduleCalendar;
  } catch (error) {
    console.error("Error loading schedule calendar:", error);
    reportAppError(error, {
      feature: "schedule_manager",
      action: "load_schedule_calendar",
      severity: "error",
      uid,
    });
    dispatch(setSchedulingDomainStatus({ domain: "scheduleCalendar", status: "error" }));
    dispatch(setSchedulingLoadError(error?.message || "Error loading schedule calendar"));
    throw error;
  }
};

export const ensureTeacherHubEventsLoaded = (uid, options = {}) => async (dispatch, getState) => {
  const { force = false } = options;
  if (!uid) return [];

  const user = selectUser(getState());
  if (user?.uid && user.uid !== uid) return null;
  if (shouldSkipSchedulingDomainLoad(getState, "teacherHubEvents", force)) {
    return user?.teacherHubEvents || [];
  }

  dispatch(setSchedulingDomainStatus({ domain: "teacherHubEvents", status: "loading" }));

  try {
    const teacherHubEvents = await fetchTeacherHubEvents(uid);
    const currentUser = selectUser(getState());
    if (!currentUser || currentUser.uid !== uid) return null;

    dispatch(patchUserTeacherHubEvents(teacherHubEvents));
    dispatch(setSchedulingDomainStatus({ domain: "teacherHubEvents", status: "loaded" }));
    return teacherHubEvents;
  } catch (error) {
    console.error("Error loading teacher hub events:", error);
    reportAppError(error, {
      feature: "teacher_hub_events",
      action: "load",
      severity: "error",
      uid,
    });
    dispatch(setSchedulingDomainStatus({ domain: "teacherHubEvents", status: "error" }));
    dispatch(setSchedulingLoadError(error?.message || "Error loading teacher hub events"));
    throw error;
  }
};

export const ensureClassStyleSchemesLoaded = (uid, options = {}) => async (dispatch, getState) => {
  const { force = false } = options;
  if (!uid) return [];

  const user = selectUser(getState());
  if (user?.uid && user.uid !== uid) return null;
  if (shouldSkipSchedulingDomainLoad(getState, "classStyleSchemes", force)) {
    return user?.classStyleSchemes || [];
  }

  dispatch(setSchedulingDomainStatus({ domain: "classStyleSchemes", status: "loading" }));

  try {
    const classStyleSchemes = await fetchClassStyleSchemes(uid);
    const currentUser = selectUser(getState());
    if (!currentUser || currentUser.uid !== uid) return null;

    dispatch(patchUserClassStyleSchemes(classStyleSchemes));
    dispatch(setSchedulingDomainStatus({ domain: "classStyleSchemes", status: "loaded" }));
    return classStyleSchemes;
  } catch (error) {
    console.error("Error loading class style schemes:", error);
    reportAppError(error, {
      feature: "class_style",
      action: "load_custom_schemes",
      severity: "error",
      uid,
    });
    dispatch(setSchedulingDomainStatus({ domain: "classStyleSchemes", status: "error" }));
    dispatch(setSchedulingLoadError(error?.message || "Error loading class style schemes"));
    throw error;
  }
};

export const ensureUserSchedulingDomains = (uid, options = {}) => async (dispatch, getState) => {
  const { force = false, prioritizeTeacherHubEvents = false } = options;
  if (!uid) return null;

  dispatch(setSchedulingLoadError(null));

  const loaders = [
    () => dispatch(ensureScheduleCalendarLoaded(uid, { force })),
    () => dispatch(ensureTeacherHubEventsLoaded(uid, { force })),
    () => dispatch(ensureClassStyleSchemesLoaded(uid, { force })),
  ];

  if (prioritizeTeacherHubEvents) {
    await dispatch(ensureTeacherHubEventsLoaded(uid, { force }));
    await Promise.all([
      dispatch(ensureScheduleCalendarLoaded(uid, { force })),
      dispatch(ensureClassStyleSchemesLoaded(uid, { force })),
    ]);
    return null;
  }

  const results = await Promise.allSettled(loaders.map((loader) => loader()));
  const failures = results.filter((result) => result.status === "rejected");
  if (failures.length) {
    console.error("One or more scheduling domains failed to load:", failures);
  }

  return null;
};

// Compatibility shim — full hydrate for OAuth callbacks and explicit reloads
export const fetchUserData = (uid) => async (dispatch) => {
  const { userData, classesData } = await dispatch(bootstrapUserShell(uid));
  await dispatch(ensureUserSchedulingDomains(uid, { force: true }));
  return { userData, classesData };
};

export const fetchShareUserData = (uid) => async (dispatch) => {
  if (!uid) {
    const missingUidError = new Error("Missing share owner user ID.");
    dispatch(setError(missingUidError.message));
    throw missingUidError;
  }

  try {
    const userRef = doc(db, "usersNew", uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const userNotFoundError = new Error(`User document not found for uid: ${uid}`);
      dispatch(setError(userNotFoundError.message));
      throw userNotFoundError;
    }

    const userData = userSnap.data();

    if (userData?.settings?.useShareCode === false) {
      const shareDisabledError = new Error("Share link is disabled for this account.");
      dispatch(setError(shareDisabledError.message));
      throw shareDisabledError;
    }

    const classesRef = collection(db, `usersNew/${uid}/classes`);
    const classesSnap = await getDocs(classesRef);
    const classesData = sortClassesByOrder(
      filterShareableClasses(mapClassesSnapshot(classesSnap)),
      filterShareableOrderOfClasses(userData.orderOfClasses)
    );

    const shareUserData = {
      uid,
      shareCode: userData.shareCode,
      settings: userData.settings,
      announcements: userData.announcements || [],
      orderOfClasses: filterShareableOrderOfClasses(userData.orderOfClasses),
      isShareSession: true,
    };

    dispatch(resetSchedulingLoad());
    dispatch(login({ userData: shareUserData, resetScheduling: true }));
    dispatch(setClasses(classesData));
    dispatch(setError(null));

    await dispatch(ensureUserSchedulingDomains(uid, { force: true }));

    return { userData: shareUserData, classesData };
  } catch (error) {
    dispatch(setError(error?.message || "Error fetching share data"));
    console.error("Error fetching share data:", error);
    throw error;
  }
};

export const fetchClassContentBoxes = (uid, classId) => (dispatch, getState) => {
  if (!uid || !classId) return;

  const localMockContentBoxes = getLocalMockSampleClassContentBoxes(classId, { user: selectUser(getState()) });
  if (localMockContentBoxes) {
    return dispatchStaticContentBoxes({ dispatch, classId, contentBoxes: localMockContentBoxes });
  }

  const gettingStartedContentBoxes = getGettingStartedContentBoxes(classId);
  if (gettingStartedContentBoxes) {
    return dispatchStaticContentBoxes({ dispatch, classId, contentBoxes: gettingStartedContentBoxes });
  }

  try {
    const contentBoxesRef = collection(db, `usersNew/${uid}/classes/${classId}/contentBoxes`);

    return onSnapshotWithMetadata(contentBoxesRef, (snapshot, freshness) => {
      if (shouldSuppressContentBoxSnapshot(snapshot, classId)) {
        return;
      }

      const contentBoxes = mapSnapshotToContentBoxes(snapshot);

      dispatch(
        setClassContentBoxes({
          classId,
          contentBoxes,
        })
      );
      dispatch(
        setContentBoxesLoad({
          classId,
          status: "loaded",
          syncSource: freshness.source,
          isFromCache: freshness.isFromCache,
          hasPendingWrites: freshness.hasPendingWrites,
          lastServerSyncAt: freshness.lastServerSyncAt,
        })
      );
    });
  } catch (error) {
    console.error("Error fetching content boxes:", error);
    reportAppError(error, {
      feature: "content_box",
      action: "load",
      severity: "error",
      classId,
      uid,
    });
  }
};

export const fetchClassContentBoxesScoped = (uid, classId, scope = {}) => (dispatch, getState) => {
  if (!uid || !classId) return;

  const scopeKey = buildContentBoxScopeKey(scope);
  const localMockContentBoxes = getLocalMockSampleClassContentBoxes(classId, { user: selectUser(getState()) });
  if (localMockContentBoxes) {
    return dispatchStaticContentBoxes({ dispatch, classId, contentBoxes: localMockContentBoxes, scopeKey });
  }

  const gettingStartedContentBoxes = getGettingStartedContentBoxes(classId);
  if (gettingStartedContentBoxes) {
    return dispatchStaticContentBoxes({ dispatch, classId, contentBoxes: gettingStartedContentBoxes, scopeKey });
  }

  const contentBoxesRef = collection(db, `usersNew/${uid}/classes/${classId}/contentBoxes`);
  const queries = buildContentBoxPresentQueries(contentBoxesRef, scope);
  const dispatchLoadingState = () => {
    dispatch(setContentBoxesLoad({
      classId,
      status: "loading",
      scopeKey,
      expectedQueryCount: queries.length,
      receivedQueryCount: 0,
    }));
  };
  dispatchLoadingState();

  const subscribe = (onSubscriptionError) => {
    const snapshotsByQueryIndex = new Map();
    const receivedQueryIndices = new Set();
    let mergedFreshness = {
      source: "unknown",
      isFromCache: true,
      hasPendingWrites: false,
      lastServerSyncAt: null,
    };

    const publishMergedSnapshot = (freshness) => {
      if (receivedQueryIndices.size < queries.length) {
        return;
      }

      if (isServerConfirmedSnapshot(freshness)) {
        mergedFreshness = freshness;
      } else if (mergedFreshness.source !== "server") {
        mergedFreshness = freshness;
      }

      const documentGroups = queries.map((_, index) =>
        mapSnapshotToContentBoxes(snapshotsByQueryIndex.get(index) || { docs: [] })
      );
      const mergedContentBoxes = mergeContentBoxDocuments(documentGroups);

      dispatch(
        setClassContentBoxes({
          classId,
          contentBoxes: mergedContentBoxes,
        })
      );
      dispatch(
        setContentBoxesLoad({
          classId,
          status: "loaded",
          scopeKey,
          syncSource: mergedFreshness.source,
          isFromCache: mergedFreshness.isFromCache,
          hasPendingWrites: mergedFreshness.hasPendingWrites,
          lastServerSyncAt: mergedFreshness.lastServerSyncAt,
          expectedQueryCount: queries.length,
          receivedQueryCount: receivedQueryIndices.size,
        })
      );
    };

    const unsubscribers = queries.map((scopedQuery, index) =>
      onSnapshotWithMetadata(
        scopedQuery,
        (snapshot, freshness) => {
          if (shouldSuppressContentBoxSnapshot(snapshot, classId)) {
            return;
          }

          snapshotsByQueryIndex.set(index, snapshot);
          receivedQueryIndices.add(index);
          dispatch(setContentBoxesLoad({
            classId,
            scopeKey,
            expectedQueryCount: queries.length,
            receivedQueryCount: receivedQueryIndices.size,
          }));
          publishMergedSnapshot(freshness);
        },
        (error) => {
          onSubscriptionError({
            error,
            queryIndex: index,
            receivedQueryCount: receivedQueryIndices.size,
          });
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (error) {
          // no-op
        }
      });
    };
  };

  let latestListenerErrorContext = {};
  const requireMatchingUid = shouldRequireMatchingAuthUid({
    isShareSession: selectUser(getState())?.isShareSession,
  });
  return subscribeWithFirestoreAuthRetry({
    auth,
    uid,
    requireMatchingUid,
    subscribe: (onError) => subscribe(({ error, queryIndex, receivedQueryCount }) => {
      latestListenerErrorContext = {
        queryIndex,
        receivedQueryCount,
      };
      onError(error);
    }),
    onRetry: dispatchLoadingState,
    onAuthMismatch: () => {
      dispatch(setContentBoxesLoad({
        classId,
        status: "error",
        scopeKey,
        expectedQueryCount: queries.length,
        receivedQueryCount: 0,
      }));
    },
    onError: (error) => {
      const listenerContext = latestListenerErrorContext;
      console.error("Error fetching scoped content boxes:", error);
      reportAppError(error, {
        feature: "present",
        action: "content_box_listener",
        severity: "error",
        dedupeKey: `present:content_box_listener:${uid}:${classId}:${scopeKey}:${listenerContext.queryIndex ?? "setup"}`,
        classId,
        uid,
        extra: {
          queryIndex: listenerContext.queryIndex ?? null,
          scopeKey,
          expectedQueryCount: queries.length,
          receivedQueryCount: listenerContext.receivedQueryCount ?? 0,
          authRetryAttempted: true,
          requireMatchingUid,
          authenticatedUidMatches: auth.currentUser?.uid === uid,
        },
      });
      dispatch(setContentBoxesLoad({
        classId,
        status: "error",
        scopeKey,
        expectedQueryCount: queries.length,
        receivedQueryCount: listenerContext.receivedQueryCount ?? 0,
      }));
    },
  });
};

export const patchUserScheduleCalendar = (scheduleCalendar) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  const nextUser = scheduleCalendar?.scheduleRuntimeMode
    ? {
        ...user,
        settings: {
          ...(user.settings || {}),
          scheduleRuntimeMode: scheduleCalendar.scheduleRuntimeMode,
          ...(scheduleCalendar.schoolYearSettings
            ? { schoolYearSettings: scheduleCalendar.schoolYearSettings }
            : {}),
          ...(scheduleCalendar.scheduleManagerSetup
            ? { scheduleManagerSetup: scheduleCalendar.scheduleManagerSetup }
            : {}),
        },
      }
    : user;
  dispatch(
    login({
      userData: nextUser,
      scheduling: { scheduleCalendar },
    })
  );
};

export const patchUserTeacherHubEvents = (teacherHubEvents) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(
    login({
      userData: user,
      scheduling: { teacherHubEvents },
    })
  );
};

export const patchUserAnnouncements = (announcements) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, announcements } }));
};

export const patchUserContentBoxTemplates = (contentBoxTemplates) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, contentBoxTemplates } }));
};

export const patchUserContentBoxTags = (contentBoxTags) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, contentBoxTags } }));
};

export const patchUserReminders = (reminders) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, reminders } }));
};

export const patchUserAutoMusic = (autoMusic) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, autoMusic } }));
};

export const patchUserAutoMusicPlaylistPointer = (playlistId, pointerIndex) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user?.autoMusic || !playlistId) return;
  dispatch(login({
    userData: {
      ...user,
      autoMusic: {
        ...user.autoMusic,
        playlistPointers: {
          ...(user.autoMusic.playlistPointers || {}),
          [playlistId]: pointerIndex,
        },
      },
    },
  }));
};

export const patchUserCountdownTimers = (countdownTimers) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, countdownTimers } }));
};

export const patchUserAutoCountdowns = (autoCountdowns) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, autoCountdowns } }));
};

export const patchUserFields = (fields) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(login({ userData: { ...user, ...fields } }));
};

export const patchUserOrderOfClasses = (orderOfClasses) => (dispatch, getState) => {
  const user = selectUser(getState());
  const classes = selectClasses(getState());
  if (!user) return;

  dispatch(login({ userData: { ...user, orderOfClasses } }));
  dispatch(setClasses(classes));
};

export const saveUserScheduleCalendar = (scheduleCalendar) => async (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user?.uid) return null;

  const scheduleCalendarStatus = selectSchedulingLoadStatus(getState())?.scheduleCalendar;
  if (!["loaded", "error"].includes(scheduleCalendarStatus)) {
    console.warn("Blocked schedule calendar save before schedule finished loading.", {
      scheduleCalendarStatus,
      uid: user.uid,
    });
    throw new Error(SCHEDULE_CALENDAR_NOT_LOADED_MESSAGE);
  }

  try {
    const savedScheduleCalendar = await saveScheduleCalendar(user.uid, scheduleCalendar, {
      existingScheduleCalendar: user.settings?.scheduleCalendar,
      userSettings: user.settings,
    });
    dispatch(patchUserScheduleCalendar(savedScheduleCalendar));
    return savedScheduleCalendar;
  } catch (error) {
    reportAppError(error, {
      feature: "schedule_manager",
      action: "save_schedule_calendar",
      severity: "error",
      uid: user.uid,
      extra: {
        templateCount: scheduleCalendar?.templates?.length || 0,
        assignmentCount: Array.isArray(scheduleCalendar?.assignments)
          ? scheduleCalendar.assignments.length
          : Object.keys(scheduleCalendar?.assignments || {}).length,
      },
    });
    throw error;
  }
};

export const saveUserTeacherHubEvents = (events) => async (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user?.uid) return [];

  const teacherHubEventsStatus = selectSchedulingLoadStatus(getState())?.teacherHubEvents;
  if (teacherHubEventsStatus !== "loaded") {
    console.warn("Blocked teacher hub event save before events finished loading.", {
      teacherHubEventsStatus,
      uid: user.uid,
    });
    throw new Error(TEACHER_HUB_EVENTS_NOT_LOADED_MESSAGE);
  }

  try {
    const savedEvents = await replaceTeacherHubEvents(user.uid, events, { userId: user.uid });
    dispatch(patchUserTeacherHubEvents(savedEvents));
    return savedEvents;
  } catch (error) {
    reportAppError(error, {
      feature: "teacher_hub_events",
      action: "save_events",
      severity: "error",
      uid: user.uid,
      extra: {
        eventCount: events?.length || 0,
      },
    });
    throw error;
  }
};

export const patchUserClassStyleSchemes = (classStyleSchemes) => (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user) return;
  dispatch(
    login({
      userData: user,
      scheduling: { classStyleSchemes },
    })
  );
};

export const saveUserClassStyleScheme = (scheme) => async (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user?.uid) return null;

  const classStyleSchemesStatus = selectSchedulingLoadStatus(getState())?.classStyleSchemes;
  if (classStyleSchemesStatus !== "loaded") {
    console.warn("Blocked class style scheme save before schemes finished loading.", {
      classStyleSchemesStatus,
      uid: user.uid,
    });
    throw new Error(CLASS_STYLE_SCHEMES_NOT_LOADED_MESSAGE);
  }

  try {
    const savedScheme = await upsertClassStyleScheme(user.uid, scheme);
    const nextSchemes = mergeClassStyleSchemeIntoList(selectClassStyleSchemes(getState()), savedScheme);
    dispatch(patchUserClassStyleSchemes(nextSchemes));
    return savedScheme;
  } catch (error) {
    reportAppError(error, {
      feature: "class_style",
      action: "save_custom_scheme",
      severity: "error",
      uid: user.uid,
      extra: {
        schemeId: scheme?.id || "",
      },
    });
    throw error;
  }
};

export const saveUserClassStyleSchemes = (schemes) => async (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user?.uid) return [];

  const classStyleSchemesStatus = selectSchedulingLoadStatus(getState())?.classStyleSchemes;
  if (classStyleSchemesStatus !== "loaded") {
    console.warn("Blocked class style scheme save before schemes finished loading.", {
      classStyleSchemesStatus,
      uid: user.uid,
    });
    throw new Error(CLASS_STYLE_SCHEMES_NOT_LOADED_MESSAGE);
  }

  try {
    const savedSchemes = await replaceClassStyleSchemes(user.uid, schemes);
    dispatch(patchUserClassStyleSchemes(savedSchemes));
    return savedSchemes;
  } catch (error) {
    reportAppError(error, {
      feature: "class_style",
      action: "save_custom_schemes",
      severity: "error",
      uid: user.uid,
      extra: {
        schemeCount: schemes?.length || 0,
      },
    });
    throw error;
  }
};

export const deleteUserClassStyleScheme = (schemeId) => async (dispatch, getState) => {
  const user = selectUser(getState());
  if (!user?.uid) return { remainingSchemes: [], resetClassIds: [] };

  const classStyleSchemesStatus = selectSchedulingLoadStatus(getState())?.classStyleSchemes;
  if (classStyleSchemesStatus !== "loaded") {
    console.warn("Blocked class style scheme delete before schemes finished loading.", {
      classStyleSchemesStatus,
      uid: user.uid,
    });
    throw new Error(CLASS_STYLE_SCHEMES_NOT_LOADED_MESSAGE);
  }

  const targetId = String(schemeId || "").trim();
  if (!targetId) {
    throw new Error("Missing class style scheme id.");
  }

  try {
    await deleteClassStyleSchemeById(user.uid, targetId);
    const remainingSchemes = (selectClassStyleSchemes(getState()) || []).filter(
      (scheme) => String(scheme?.id || "").trim() !== targetId
    );
    dispatch(patchUserClassStyleSchemes(remainingSchemes));

    const fallbackUpdates = buildCustomSchemeDeleteFallbackUpdates({
      classes: selectClasses(getState()),
      schemeId: targetId,
      user,
    });
    const resetClassIds = [];

    await Promise.all(
      fallbackUpdates.map(async ({ classId, classItem, payload }) => {
        const classRef = doc(db, `usersNew/${user.uid}/classes/${classId}`);
        await updateDoc(classRef, payload);
        dispatch(setSingleClass({ ...classItem, ...payload }));
        resetClassIds.push(classId);
      })
    );

    return { remainingSchemes, resetClassIds };
  } catch (error) {
    reportAppError(error, {
      feature: "class_style",
      action: "delete_custom_scheme",
      severity: "error",
      uid: user.uid,
      extra: {
        schemeId: targetId,
      },
    });
    throw error;
  }
};

const setAtPath = (obj, keys, value) => {
  if (!obj || keys.length === 0) return obj;
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [first, ...remaining] = keys;
  return {
    ...obj,
    [first]: setAtPath({ ...(obj[first] || {}) }, remaining, value),
  };
};

export const patchClassByPath = (classId, path, value) => (dispatch, getState) => {
  const classes = selectClasses(getState());
  const existingClass = classes.find((c) => c.classId === classId);
  if (!existingClass) return;
  dispatch(setSingleClass(setAtPath(existingClass, path.split('.'), value)));
};

export const fetchClassData = (uid, classId) => async (dispatch) => {
  if (!uid || !classId) return;

  try {
    const classRef = doc(db, `usersNew/${uid}/classes/${classId}`);
    const classSnap = await getDoc(classRef);

    if (classSnap.exists()) {
      const classData = { classId: classSnap.id, ...classSnap.data() };
      dispatch(setSingleClass(classData));
    }
  } catch (error) {
    console.error("Error fetching class data:", error);
    dispatch(setError("Error refreshing class data"));
  }
};
